import React from "react";
import { Card, Col, Row, Statistic, Progress, Divider, Table, Tabs } from "antd";
import { UsergroupAddOutlined, BarChartOutlined, PieChartOutlined, TeamOutlined } from "@ant-design/icons";

const { TabPane } = Tabs;
const GReportsummary = (props) => {
  const { summaryData } = props;
  
  if (!summaryData) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case "read": return "#52c41a";
      case "delivered": return "#1890ff";
      case "sent": return "#faad14";
      case "failed": return "#ff4d4f";
      default: return "#8c8c8c";
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case "marketing": return "#fa541c";
      case "utility": return "#13c2c2";
      case "service": return "#722ed1";
      default: return "#8c8c8c";
    }
  };

  return (
    <div >
      {/* Compact Overview Stats */}
      <Row gutter={[12, 12]} style={{ marginBottom: "16px" }}>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ textAlign: "center", background: "#f6ffed", border: "1px solid #b7eb8f" }}>
            <Statistic
              title="Total"
              value={summaryData.total_reports}
              valueStyle={{ color: "#52c41a", fontSize: "16px" }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ textAlign: "center", background: "#e6f7ff", border: "1px solid #91d5ff" }}>
            <Statistic
              title="Out Reports"
              value={summaryData.out_reports_count}
              valueStyle={{ color: "#1890ff", fontSize: "16px" }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ textAlign: "center", background: "#fff2e8", border: "1px solid #ffbb96" }}>
            <Statistic
              title="Campaigns"
              value={summaryData.campaign_reports_count}
              valueStyle={{ color: "#fa8c16", fontSize: "16px" }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ textAlign: "center", background: "#f9f0ff", border: "1px solid #d3adf7" }}>
            <Statistic
              title="Date Range"
              value={
                summaryData.date_range?.start_date && summaryData.date_range?.end_date ? (
                  (() => {
                    const startDate = new Date(summaryData.date_range.start_date).toLocaleDateString();
                    const endDate = new Date(summaryData.date_range.end_date).toLocaleDateString();
                    
                    if (startDate === endDate) {
                      return startDate;
                    } else {
                      return `${startDate} - ${endDate}`;
                    }
                  })()
                ) : "All Time"
              }
              valueStyle={{ color: "#722ed1", fontSize: "16px" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Tabbed Content for Better Organization */}
      <Tabs defaultActiveKey="1" size="small" centered>
        <Tabs.TabPane tab={<span><BarChartOutlined />Status</span>} key="1">
          <Row gutter={[8, 8]}>
            {Object.entries(summaryData.status_breakdown || {}).map(([status, count]) => {
              const total = summaryData.total_reports;
              const percentage = ((count / total) * 100).toFixed(1);
              
              return (
                <Col xs={12} sm={8} md={6} key={status}>
                  <Card size="small" style={{ textAlign: "center", height: "120px" }}>
                    <div style={{
                      color: getStatusColor(status),
                      fontWeight: "500",
                      textTransform: "capitalize",
                      marginBottom: "4px",
                      fontSize: "12px"
                    }}>
                      {status}
                    </div>
                    <Statistic value={count} valueStyle={{ fontSize: "14px" }} />
                    <Progress
                      percent={parseFloat(percentage)}
                      showInfo={false}
                      strokeColor={getStatusColor(status)}
                      size="small"
                      style={{ marginTop: "4px" }}
                    />
                    <div style={{ fontSize: "10px", color: "#8c8c8c" }}>
                      {percentage}%
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Tabs.TabPane>

        <Tabs.TabPane tab={<span><PieChartOutlined />Category</span>} key="2">
          <Row gutter={[8, 8]}>
            {Object.entries(summaryData.category_breakdown || {}).map(([category, count]) => {
              const total = summaryData.total_reports;
              const percentage = ((count / total) * 100).toFixed(1);
              
              return (
                <Col xs={12} sm={8} md={6} key={category}>
                  <Card size="small" style={{ textAlign: "center", height: "120px" }}>
                    <div style={{
                      color: getCategoryColor(category),
                      fontWeight: "500",
                      textTransform: "capitalize",
                      marginBottom: "4px",
                      fontSize: "12px"
                    }}>
                      {category}
                    </div>
                    <Statistic value={count} valueStyle={{ fontSize: "14px" }} />
                    <Progress
                      percent={parseFloat(percentage)}
                      showInfo={false}
                      strokeColor={getCategoryColor(category)}
                      size="small"
                      style={{ marginTop: "4px" }}
                    />
                    <div style={{ fontSize: "10px", color: "#8c8c8c" }}>
                      {percentage}%
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Tabs.TabPane>
        <Tabs.TabPane tab={<span><TeamOutlined />Users</span>} key="3">
            <Table
              size="small"
              dataSource={summaryData.user_breakdown || []}
              pagination={{ pageSize: 10, showSizeChanger: false, size: "small" }}
              rowKey="user_id"
              columns={[
                {
                  title: "User",
                  dataIndex: "company_name",
                  key: "company_name",
                  width: "40%",
                  render: (company_name) => (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <UsergroupAddOutlined style={{ marginRight: "4px", color: "#1890ff", fontSize: "12px" }} />
                      <span style={{ fontWeight: "500", fontSize: "12px" }}>{company_name}</span>
                    </div>
                  ),
                },
                {
                  title: "Total",
                  dataIndex: "count",
                  key: "count",
                  width: "20%",
                  sorter: (a, b) => b.count - a.count,
                  render: (count) => (
                    <span style={{ fontWeight: "500", fontSize: "12px" }}>{count}</span>
                  ),
                },
                {
                  title: "Out",
                  dataIndex: "out_reports",
                  key: "out_reports",
                  width: "20%",
                  render: (count) => (
                    <span style={{ color: "#1890ff", fontSize: "11px" }}>{count}</span>
                  ),
                },
                {
                  title: "Campaign",
                  dataIndex: "campaign_reports",
                  key: "campaign_reports",
                  width: "20%",
                  render: (count) => (
                    <span style={{ color: "#fa8c16", fontSize: "11px" }}>{count}</span>
                  ),
                },
              ]}
            />
        </Tabs.TabPane>
      </Tabs>
    </div>
  );
};

export default GReportsummary;
